/**
 * src/components/Layout/TopNav.tsx -- Desktop horizontal top navigation.
 *
 * Previously also hosted a mobile hamburger menu; that has been removed in
 * favor of the BottomTabBar component which is the primary navigation on
 * mobile. This header is now hidden entirely on viewports <=767px via the
 * .topnav-header CSS class + media query in globals.css.
 *
 * Sign-out moved to the Profile page footer when the hamburger went away --
 * it is no longer reachable from the top bar.
 *
 * Role-aware nav links (WP-ATTICDEV-032, AC-003):
 *   Customer (default / null / loading):
 *     Dashboard / Search / Orders / Storage / Profile
 *   Driver / Both:
 *     Jobs / Active / Earnings / Profile
 *
 * Loading safe-default: while useCustomerRole is fetching, falls back to the
 * customer link set so customer accounts never flash driver links.
 */

import React from "react";
import { Link, useLocation } from "react-router";
import {
  Package,
  LogIn,
  User,
  ClipboardList,
  Truck,
  LayoutDashboard,
  Shield,
  Layers,
  AlertTriangle,
  CheckSquare,
  Droplet,
} from '../icons';
import { useAuth } from "../../context/AuthContext";
import { useCustomerRole } from "../../hooks/useCustomerRole";
import { Logo } from "../ui/Logo";

export function TopNav() {
  const { isAuthenticated } = useAuth();
  const { role, loading } = useCustomerRole();
  const location = useLocation();

  const isActive = (path: string) =>
    path.startsWith("/driver/")
      ? location.pathname.startsWith(path)
      : location.pathname === path;

  const dlStyle = (path: string): React.CSSProperties => ({
    color: isActive(path) ? "var(--color-primary)" : "var(--color-text-muted)",
    textDecoration: "none",
    fontSize: 'var(--fs-sm)',
    fontWeight: 500,
    padding: "var(--space-1) 0",
    borderBottom: isActive(path) ? "2px solid var(--color-primary)" : "2px solid transparent",
    transition: "color 0.15s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minHeight: "44px",
  });

  const isDriverRole = !loading && (role === "driver" || role === "both");
  const isAdminRole = !loading && role === "admin";

  // TopNav is desktop chrome for admins only. Authenticated customers and
  // drivers navigate exclusively via the BottomTabBar — rendering both
  // surfaces was redundant. Unauthenticated users still see the TopNav so
  // /login and the landing have the Attic wordmark + sign-in link.
  //
  // Phase 4 admin command center: /admin/* renders inside AdminShell which
  // has its own sidebar + topbar, so the global TopNav would be a second,
  // redundant chrome above it. Skip TopNav on admin routes.
  if (isAuthenticated && !isAdminRole) return null;
  if (isAdminRole && location.pathname.startsWith('/admin')) return null;

  const ADMIN_LINKS = (
    <>
      <Link to="/admin/inventory" data-testid="nav-admin-inventory" aria-current={isActive("/admin/inventory") ? "page" : undefined} style={dlStyle("/admin/inventory")}>
        <Package size={16} aria-hidden="true" /> Crates
      </Link>
      <Link to="/admin/orders" data-testid="nav-admin-orders" aria-current={isActive("/admin/orders") ? "page" : undefined} style={dlStyle("/admin/orders")}>
        <ClipboardList size={16} aria-hidden="true" /> Orders
      </Link>
      <Link to="/admin/storage" data-testid="nav-admin-storage" aria-current={isActive("/admin/storage") ? "page" : undefined} style={dlStyle("/admin/storage")}>
        <Layers size={16} aria-hidden="true" /> Storage
      </Link>
      <Link to="/admin/audit" data-testid="nav-admin-audit" aria-current={isActive("/admin/audit") ? "page" : undefined} style={dlStyle("/admin/audit")}>
        <CheckSquare size={16} aria-hidden="true" /> Audit
      </Link>
      <Link to="/admin/exceptions" data-testid="nav-admin-exceptions" aria-current={isActive("/admin/exceptions") ? "page" : undefined} style={dlStyle("/admin/exceptions")}>
        <AlertTriangle size={16} aria-hidden="true" /> Exceptions
      </Link>
      <Link to="/admin/pools" data-testid="nav-admin-pools" aria-current={isActive("/admin/pools") ? "page" : undefined} style={dlStyle("/admin/pools")}>
        <Droplet size={16} aria-hidden="true" /> Pools
      </Link>
      <Link to="/profile" data-testid="nav-admin-profile" aria-current={isActive("/profile") ? "page" : undefined} style={dlStyle("/profile")}>
        <Shield size={16} aria-hidden="true" /> Profile
      </Link>
    </>
  );

  const DRIVER_LINKS = (
    <>
      <Link to="/driver/home" data-testid="nav-driver-home" aria-current={isActive("/driver/home") ? "page" : undefined} style={dlStyle("/driver/home")}>
        <LayoutDashboard size={16} aria-hidden="true" /> Home
      </Link>
      <Link to="/driver/active" data-testid="nav-driver-drive" aria-current={isActive("/driver/active") ? "page" : undefined} style={dlStyle("/driver/active")}>
        <Truck size={16} aria-hidden="true" /> Drive
      </Link>
      <Link to="/profile" data-testid="nav-profile" aria-current={isActive("/profile") ? "page" : undefined} style={dlStyle("/profile")}>
        <User size={16} aria-hidden="true" /> Profile
      </Link>
    </>
  );

  const CUSTOMER_LINKS = (
    <>
      <Link to="/dashboard" data-testid="nav-dashboard" aria-current={isActive("/dashboard") ? "page" : undefined} style={dlStyle("/dashboard")}>Dashboard</Link>
      <Link to="/profile" data-testid="nav-profile" aria-current={isActive("/profile") ? "page" : undefined} style={dlStyle("/profile")}><User size={16} aria-hidden="true" /> Profile</Link>
    </>
  );

  const AUTH_LINKS_DESKTOP = isAuthenticated ? (
    isAdminRole ? ADMIN_LINKS : isDriverRole ? DRIVER_LINKS : CUSTOMER_LINKS
  ) : (
    <Link to="/login" data-testid="nav-sign-in"
      style={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--color-primary)",
        textDecoration: "none", fontSize: 'var(--fs-sm)', fontWeight: 500 }}>
      <LogIn size={16} aria-hidden="true" /> Sign in
    </Link>
  );

  /* The auth pages pin themselves light (see .attic-auth in tokens.css). This
     bar renders ABOVE the route content and so sits outside that scope, which
     on a dark-mode machine left a dark header sitting on a light sign-in page.
     A seam like that reads worse than either theme on its own, so the bar
     joins the auth scope on exactly those two routes. */
  // /signup redirects to /login, so /login is the whole auth surface here.
  const onAuthRoute = location.pathname === "/login";
  const headerClass = [
    "topnav-header",
    isAdminRole ? "topnav-header--admin" : "",
    onAuthRoute ? "attic-auth" : "",
  ].filter(Boolean).join(" ");

  return (
    <header
      className={headerClass}
      style={{
        backgroundColor: "var(--color-surface)",
        borderBottom: "1px solid var(--color-border)",
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      <nav style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "0 var(--space-6)",
        height: "60px",
      }}>
        <Link to="/" style={{ textDecoration: "none" }}>
          <Logo size="sm" showWordmark align="start" />
        </Link>

        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-6)" }}>
          {AUTH_LINKS_DESKTOP}
        </div>
      </nav>
    </header>
  );
}
